import React, { useState } from 'react';
import { X, Share2, Download } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import t from '../translations';

const SIGN_SYMBOLS = {
  Mesh:'♈', Vrishabh:'♉', Mithun:'♊', Kark:'♋',
  Simha:'♌', Kanya:'♍', Tula:'♎', Vrishchik:'♏',
  Dhanu:'♐', Makar:'♑', Kumbh:'♒', Meen:'♓',
};

/* ── Helpers ── */
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawStars(ctx, W, H) {
  for (let i = 0; i < 80; i++) {
    ctx.beginPath();
    ctx.arc(Math.random() * W, Math.random() * H, Math.random() * 1.2 + 0.3, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(255,255,255,${(Math.random() * 0.4 + 0.05).toFixed(2)})`;
    ctx.fill();
  }
}

/* Wrap text, return next Y */
function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines = 3) {
  const words = (text || '').split(' ');
  let line = '';
  let linesDrawn = 0;
  for (const word of words) {
    const test = line + word + ' ';
    if (ctx.measureText(test).width > maxWidth && line !== '') {
      ctx.fillText(line.trim(), x, y);
      y += lineHeight;
      linesDrawn++;
      line = word + ' ';
      if (linesDrawn >= maxLines - 1) {
        // Remaining goes on last line with ellipsis
        const remaining = words.slice(words.indexOf(word) + 1).join(' ');
        const lastLine = word + (remaining ? ' ' + remaining : '');
        let truncated = lastLine;
        while (ctx.measureText(truncated + '…').width > maxWidth && truncated.length > 0) {
          truncated = truncated.slice(0, -1);
        }
        ctx.fillText(truncated + '…', x, y);
        y += lineHeight;
        return y;
      }
    } else {
      line = test;
    }
  }
  if (line.trim()) { ctx.fillText(line.trim(), x, y); y += lineHeight; }
  return y;
}

/* ── Card generation ── */
async function buildCard(reading, profile) {
  const W = 430, H = 780;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Wait for web fonts
  await document.fonts.ready;

  const G = '#C9A84C', BG = '#0A0F1E', CR = '#F5F0E8', MU = '#8A8580';
  const PAD = 36;
  const { colorReading: cr, foodReading: fr, routineReading: rr } = reading;
  const chart = profile?.chart;

  // ── Background ──
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);
  drawStars(ctx, W, H);

  // ── Subtle radial glow behind title ──
  const glow = ctx.createRadialGradient(W / 2, 100, 20, W / 2, 100, 180);
  glow.addColorStop(0, 'rgba(201,168,76,0.08)');
  glow.addColorStop(1, 'transparent');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, 200);

  // ── Outer border ──
  roundRect(ctx, 14, 14, W - 28, H - 28, 18);
  ctx.strokeStyle = 'rgba(201,168,76,0.3)';
  ctx.lineWidth = 1;
  ctx.stroke();

  // ── Top gold line ──
  const tg = ctx.createLinearGradient(0, 0, W, 0);
  tg.addColorStop(0, 'transparent');
  tg.addColorStop(0.35, G);
  tg.addColorStop(0.65, G);
  tg.addColorStop(1, 'transparent');
  ctx.strokeStyle = tg;
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(52, 38); ctx.lineTo(W - 52, 38); ctx.stroke();

  // ── Star icon ──
  ctx.font = '18px sans-serif';
  ctx.fillStyle = G;
  ctx.textAlign = 'center';
  ctx.fillText('✦', W / 2, 76);

  // ── App name ──
  ctx.font = 'bold 30px "Playfair Display", Georgia, serif';
  ctx.fillStyle = G;
  ctx.fillText('DailyRaashi', W / 2, 114);

  // ── Date ──
  const dateStr = new Date().toLocaleDateString('en-IN',
    { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  ctx.font = '12px Inter, Arial, sans-serif';
  ctx.fillStyle = MU;
  ctx.fillText(dateStr, W / 2, 136);

  // ── Divider ──
  ctx.strokeStyle = 'rgba(201,168,76,0.12)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(52, 152); ctx.lineTo(W - 52, 152); ctx.stroke();

  // ── Moon sign + Ascendant ──
  let y = 178;
  if (chart) {
    const moonSym  = SIGN_SYMBOLS[chart.moonSign?.sa]  || '☽';
    const ascSym   = SIGN_SYMBOLS[chart.ascendant?.sa] || '↑';

    // Left: Moon
    ctx.textAlign = 'left';
    ctx.font = '20px sans-serif';
    ctx.fillStyle = G;
    ctx.fillText(moonSym, PAD, y + 20);

    ctx.font = '9px Inter, Arial, sans-serif';
    ctx.fillStyle = MU;
    ctx.fillText('CHANDRA / MOON', PAD + 28, y + 6);

    ctx.font = 'bold 14px Inter, Arial, sans-serif';
    ctx.fillStyle = CR;
    ctx.fillText(`${chart.moonSign?.sa || '—'} / ${chart.moonSign?.en || ''}`, PAD + 28, y + 22);

    // Right: Ascendant
    ctx.textAlign = 'right';
    ctx.font = '20px sans-serif';
    ctx.fillStyle = G;
    ctx.fillText(ascSym, W - PAD, y + 20);

    ctx.font = '9px Inter, Arial, sans-serif';
    ctx.fillStyle = MU;
    ctx.fillText('LAGNA / ASCENDANT', W - PAD - 28, y + 6);

    ctx.font = 'bold 14px Inter, Arial, sans-serif';
    ctx.fillStyle = CR;
    ctx.fillText(`${chart.ascendant?.sa || '—'} / ${chart.ascendant?.en || ''}`, W - PAD - 28, y + 22);

    y += 54;
  }

  // ── Divider ──
  ctx.strokeStyle = 'rgba(201,168,76,0.1)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(52, y + 4); ctx.lineTo(W - 52, y + 4); ctx.stroke();
  y += 20;

  // ── Colour section ──
  if (cr) {
    ctx.textAlign = 'left';
    ctx.font = '9px Inter, Arial, sans-serif';
    ctx.fillStyle = G;
    ctx.fillText("TODAY'S COLOUR", PAD, y);
    y += 12;

    // Swatch
    const swH = 56;
    roundRect(ctx, PAD, y, W - PAD * 2, swH, 10);
    ctx.fillStyle = cr.hexCode || G;
    ctx.fill();

    // Subtle inner border on swatch
    roundRect(ctx, PAD, y, W - PAD * 2, swH, 10);
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Colour name centred on swatch
    ctx.font = 'bold 15px "Playfair Display", Georgia, serif';
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.textAlign = 'center';
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 6;
    ctx.fillText(cr.name || '', W / 2, y + swH / 2 + 5);
    ctx.shadowBlur = 0;
    y += swH + 16;
  }

  // ── Food section ──
  const foodItem = fr?.eat?.[0];
  if (foodItem) {
    ctx.textAlign = 'left';
    ctx.font = '9px Inter, Arial, sans-serif';
    ctx.fillStyle = G;
    ctx.fillText('FAVOUR TODAY', PAD, y);
    y += 14;

    ctx.font = 'bold 14px Inter, Arial, sans-serif';
    ctx.fillStyle = CR;
    ctx.fillText(`✓  ${foodItem.item}`, PAD, y);
    y += 18;

    ctx.font = '12px Inter, Arial, sans-serif';
    ctx.fillStyle = MU;
    y = wrapText(ctx, foodItem.reason || '', PAD, y, W - PAD * 2, 17, 2);
    y += 8;
  }

  // ── Divider ──
  ctx.strokeStyle = 'rgba(201,168,76,0.1)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(52, y); ctx.lineTo(W - 52, y); ctx.stroke();
  y += 16;

  // ── Routine section ──
  const morning = rr?.morning;
  if (morning) {
    ctx.textAlign = 'left';
    ctx.font = '9px Inter, Arial, sans-serif';
    ctx.fillStyle = G;
    ctx.fillText('🌅  MORNING GUIDANCE', PAD, y);
    y += 14;

    ctx.font = '13px Inter, Arial, sans-serif';
    ctx.fillStyle = CR;
    y = wrapText(ctx, morning, PAD, y, W - PAD * 2, 18, 3);
    y += 8;
  }

  // ── Bottom tagline ──
  const tagY = H - 40;
  ctx.strokeStyle = 'rgba(201,168,76,0.15)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(52, tagY - 14); ctx.lineTo(W - 52, tagY - 14); ctx.stroke();

  ctx.font = 'italic 13px "Playfair Display", Georgia, serif';
  ctx.fillStyle = G;
  ctx.textAlign = 'center';
  ctx.fillText('"Your stars. Your day."', W / 2, tagY + 4);

  // ── Bottom gold line ──
  const bg2 = ctx.createLinearGradient(0, 0, W, 0);
  bg2.addColorStop(0, 'transparent');
  bg2.addColorStop(0.5, G);
  bg2.addColorStop(1, 'transparent');
  ctx.strokeStyle = bg2;
  ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(52, H - 22); ctx.lineTo(W - 52, H - 22); ctx.stroke();

  return canvas;
}

/* ── ShareCard component ── */
export default function ShareCard({ reading, profile, onClose }) {
  const { lang } = useLang();
  const tr = t[lang];

  const [generating, setGenerating]   = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState(null);
  const [shareError, setShareError]   = useState('');

  const generate = async () => {
    setGenerating(true);
    setShareError('');
    try {
      const canvas = await buildCard(reading, profile);
      setImageDataUrl(canvas.toDataURL('image/png'));
    } catch (err) {
      console.error(err);
      setShareError('Could not generate card. Please try again.');
    }
    setGenerating(false);
  };

  const handleShare = async () => {
    if (!imageDataUrl) return;
    try {
      const res  = await fetch(imageDataUrl);
      const blob = await res.blob();
      const file = new File([blob], 'dailyraashi-reading.png', { type: 'image/png' });

      if (navigator.share && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'My DailyRaashi Reading',
          text: 'Your stars. Your day. — DailyRaashi',
        });
      } else {
        // Fallback: trigger download
        const a = document.createElement('a');
        a.href = imageDataUrl;
        a.download = 'dailyraashi-reading.png';
        a.click();
      }
    } catch (err) {
      if (err.name !== 'AbortError') setShareError('Share failed. Try saving the image instead.');
    }
  };

  const handleSave = () => {
    if (!imageDataUrl) return;
    const a = document.createElement('a');
    a.href = imageDataUrl;
    a.download = 'dailyraashi-reading.png';
    a.click();
  };

  // Auto-generate on mount
  React.useEffect(() => { generate(); }, []);

  return (
    <div
      data-testid="share-card-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(8,12,26,0.96)',
        display: 'flex', flexDirection: 'column',
        backdropFilter: 'blur(6px)',
        overflowY: 'auto',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '52px 20px 16px',
        borderBottom: '1px solid rgba(201,168,76,0.1)',
        flexShrink: 0,
      }}>
        <div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 18,
            fontWeight: 700, color: '#F5F0E8' }}>{tr.shareCardTitle}</h3>
          <p style={{ fontSize: 12, color: '#8A8580', marginTop: 2 }}>{tr.shareCardDesc}</p>
        </div>
        <button
          data-testid="share-card-close"
          onClick={onClose}
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 10, padding: 8, cursor: 'pointer', color: '#F5F0E8' }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Card preview */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', padding: '20px 20px 0', gap: 16 }}>

        {generating ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', flex: 1, gap: 16, paddingTop: 60 }}>
            <img
              src="https://static.prod-images.emergentagent.com/jobs/a18daceb-ae2c-4f94-b376-8eade8d6511f/images/fecd75b0b7001ec8bb23d55cb7dd749f25eee7eba8a99200062f858dbf83ba5e.png"
              alt="generating"
              className="rotate-mandala"
              style={{ width: 80, height: 80, opacity: 0.7 }}
            />
            <p className="shimmer-text" style={{ fontSize: 14 }}>{tr.generatingCard}</p>
          </div>
        ) : imageDataUrl ? (
          <>
            {/* Preview image — long-press to save on mobile */}
            <div style={{
              width: '100%', maxWidth: 380,
              borderRadius: 16,
              overflow: 'hidden',
              border: '1px solid rgba(201,168,76,0.25)',
              boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
            }}>
              <img
                data-testid="share-card-preview"
                src={imageDataUrl}
                alt="Your daily reading card"
                style={{ width: '100%', display: 'block' }}
              />
            </div>
            <p style={{ fontSize: 11, color: '#8A8580', textAlign: 'center' }}>
              Long-press the card to save on iOS · Tap Save on Android
            </p>
          </>
        ) : null}

        {shareError && (
          <p style={{ color: '#C0392B', fontSize: 13, textAlign: 'center' }}>{shareError}</p>
        )}
      </div>

      {/* Action buttons */}
      {imageDataUrl && !generating && (
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 12,
          flexShrink: 0 }}>
          <button
            data-testid="share-via-btn"
            className="btn-primary"
            onClick={handleShare}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <Share2 size={16} />
            {tr.shareBtn}
          </button>
          <button
            data-testid="save-card-btn"
            className="btn-secondary"
            onClick={handleSave}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <Download size={16} />
            {tr.saveBtn}
          </button>
        </div>
      )}
    </div>
  );
}
